import React from "react";
import classnames from "classnames";

import { Brand } from "custom-types/Brand";

import Classification from "components/botanic/Classification";
import StarRating from "components/botanic/StarRating";
import Image from "components/Image";

type Props = {
  brand: Brand;
};

const BrandFeaturedProducts = ({ brand }: Props) => {
  const [featuredProducts, setFeaturedProducts] = React.useState(
    brand.featuredProducts
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const idSearch = (id: number, productCategories: any[] | undefined): any => {
    if (!productCategories?.length) {
      return {};
    }
    for (let i = 0; i < productCategories?.length; i++) {
      const match = productCategories[i].products?.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
        (p: any) => p.id === id
      );
      if (match.length) {
        return match[0];
      }
    }
    return {};
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const hasReviews = (product: any) => {
    return (
      product?.avgApprovedPublicReviewRating &&
      product?.countApprovedPublicReviews
    );
  };

  // adding extra props here so we don't block render if the brand has 1000000 products
  React.useEffect(() => {
    const mappedProducts = brand.featuredProducts.map((product) => {
      if (
        product.customize.showStrainCategory ||
        product.customize.showCannabinoids ||
        product.customize.showReviews
      ) {
        const extraProps = idSearch(product.id, brand.productsByCategory);
        return { ...product, ...extraProps };
      } else {
        return product;
      }
    });
    setFeaturedProducts(mappedProducts);
  }, [brand]);

  return (
    <div className="container">
      <h2 className="mb-sm">Spotlight Products</h2>
      <div className="row">
        {
          // the product could have extra properties assigned, so cast as any
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
          featuredProducts.map((product: any) => (
            <div key={product.id} className="my-sm md:col-4">
              <div className="relative">
                <a href={`/brands/${brand.slug}/products/${product.slug}`}>
                  <Image
                    sizes={[512, 735, 238, 323, null, 512]}
                    className="featured-product-image"
                    src={product.imageUrl}
                    alt={product.name}
                    disableLazyLoad
                  />
                </a>
                {product.customize.showHotDot && (
                  <div className="hot-dot text-white text-center">HOT!</div>
                )}
                <div
                  className={classnames("bottom-overlay text-sm", {
                    "bottom-overlay-visible":
                      product.customize.showProductName ||
                      product.customize.showCannabinoids ||
                      product.customize.showStrainCategory,
                  })}
                >
                  {(product.customize.showStrainCategory ||
                    product.customize.showCannabinoids) &&
                    product.strain && (
                      <div className="flex mb-xs">
                        {product.customize.showStrainCategory &&
                          product.strain.phenotype && (
                            <Classification
                              variant="phenotype"
                              className="mr-xs"
                            >
                              {product.strain.phenotype}
                            </Classification>
                          )}
                        {product.customize.showCannabinoids &&
                          product.strain.cannabinoids && (
                            <>
                              <Classification
                                variant="cannabinoid"
                                className="mr-xs"
                              >
                                {`THC ${
                                  product.strain.cannabinoids?.thc?.percentile50
                                    ? `${product.strain.cannabinoids.thc.percentile50}%`
                                    : "—"
                                }`}
                              </Classification>
                              <Classification variant="cannabinoid">
                                {`CBD ${
                                  product.strain.cannabinoids?.cbd?.percentile50
                                    ? `${product.strain.cannabinoids.cbd.percentile50}%`
                                    : "—"
                                }`}
                              </Classification>
                            </>
                          )}
                      </div>
                    )}

                  {product.customize.showProductName && (
                    <div className="font-semibold underline mb-xs clamp2">
                      <a
                        href={`/brands/${brand.slug}/products/${product.slug}`}
                      >
                        {product.name}
                      </a>
                    </div>
                  )}

                  {product.customize.showReviews && hasReviews(product) && (
                    <div className="mb-xs">
                      <StarRating
                        ratingCount={product.countApprovedPublicReviews}
                        rating={product.avgApprovedPublicReviewRating}
                        starWidth={12}
                        as="span"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        }
      </div>
      <style jsx>{`
        .featured-product-image {
          width: 100%;
        }

        .bottom-overlay {
          display: none;
          position: absolute;
          bottom: 0px;
          left: 0px;
          min-height: 100px;
          background-color: rgba(255, 255, 255, 0.9);
          width: 100%;
          padding: 10px 10px 0;
        }

        .bottom-overlay-visible {
          display: block;
        }

        .hot-dot {
          height: 48px;
          width: 48px;
          background-color: #aa0061;
          border-radius: 50%;
          position: absolute;
          top: 10px;
          left: 10px;
          line-height: 48px;
        }

        .clamp2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BrandFeaturedProducts;
